import { useRef } from 'react';
import { GetStaticPathsResult, GetStaticPropsContext } from 'next';

import { Box, Divider, Flex } from '@chakra-ui/react';

import { ContentContainer } from '~/components/container';
import { Markdown } from '~/components/markdown';
import { SanityImage } from '~/components/sanity-image';
import { ShapesBanner } from '~/components/shapes-banner';
import { PageCategory } from '~/features/analytics/constants';
import { trackEvent } from '~/features/analytics/track-event';
import { CaseStudyCompanyCard } from '~/features/case-study/components/case-study-company-card';
import { CaseStudyHero } from '~/features/case-study/components/case-study-hero';
import { CaseStudyProcessStepCard } from '~/features/case-study/components/case-study-process-card';
import { CaseStudySocialLinks } from '~/features/case-study/components/case-study-social-links';
import { CaseStudyTestimonialCard } from '~/features/case-study/components/case-study-testimonial-card';
import { getCaseStudyContent } from '~/features/case-study/controllers/get-case-study-content';
import { CaseStudyContent } from '~/features/case-study/models/case-study-content';
import { CaseStudyForm } from '~/features/case-study-v2/components/case-study-form';
import { CaseStudyHero as CaseStudyV2Hero } from '~/features/case-study-v2/components/case-study-hero';
import { OutcomeHighlightsSection } from '~/features/case-study-v2/components/highlights-section';
import { OutcomeSummary } from '~/features/case-study-v2/components/outcome-summary';
import { OutcomeSummaryCard } from '~/features/case-study-v2/components/outcome-summary-card';
import { ProcessStep } from '~/features/case-study-v2/components/process-step';
import { ProcessStepsTableOfContents } from '~/features/case-study-v2/components/process-steps-table-of-contents';
import { RelatedCaseStudiesSection } from '~/features/case-study-v2/components/related-case-studies-section';
import { ShareBar } from '~/features/case-study-v2/components/share-bar';
import { getCaseStudyContent as getCaseStudyV2Content } from '~/features/case-study-v2/controllers/get-case-study-content';
import { CaseStudyContent as CaseStudyV2Content } from '~/features/case-study-v2/models/case-study-content';
import { logger } from '~/features/logging/logger';
import { PageHead } from '~/features/page/components/page-head';
import { PageLayout } from '~/features/page/components/page-layout';
import { getPage } from '~/features/page/controllers/get-page';
import { Page } from '~/features/page/models/page';

export default function CaseStudyPage({
  page,
  caseStudyContent,
  caseStudyV2Content,
}: {
  page: Page;
  caseStudyContent: CaseStudyContent | null;
  caseStudyV2Content: CaseStudyV2Content | null;
}) {
  const formRef = useRef<HTMLDivElement>(null);

  function handlePrimaryCtaClick() {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    trackEvent({
      type: 'click',
      name: 'Case Study CTA Clicked',
      label: 'case_study_cta_clicked',
      value: caseStudyV2Content?.slug,
    });
  }

  function handleDownloadPdfClick() {
    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    trackEvent({
      type: 'click',
      name: 'Case Study CTA Clicked',
      label: 'case_study_cta_clicked',
      value: caseStudyV2Content?.slug,
    });
  }

  if (caseStudyV2Content) {
    const fileUrl = caseStudyV2Content.pdfFile.asset.url;

    return (
      <>
        <PageHead page={page} pageCategory={PageCategory.CaseStudy} />
        <PageLayout withoutVerticalPadding>
          <CaseStudyV2Hero
            companyLogo={caseStudyV2Content.company.lofiLogoLight}
            companyName={caseStudyV2Content.company.name}
            heroImage={caseStudyV2Content.heroImage}
            title={caseStudyV2Content.title}
          />

          <Box backgroundColor='white'>
            <ShareBar onDownloadPdfClick={handleDownloadPdfClick} />

            <ContentContainer marginY={{ base: '40px', md: '96px' }}>
              <Box display='flex' flexDirection={{ base: 'column', md: 'row' }} gap={{ base: '24px', md: '32px' }}>
                <OutcomeSummary
                  flex='1'
                  text={caseStudyV2Content.outcomeSummary.body}
                  title={caseStudyV2Content.outcomeSummary.title}
                />
                <OutcomeSummaryCard
                  outcomeSummaryList={caseStudyV2Content.outcomeSummaryCardList}
                  width={{ base: '100%', sm: '352px' }}
                />
              </Box>
            </ContentContainer>

            <OutcomeHighlightsSection outcomeHighlights={caseStudyV2Content.outcomeHighlights} />

            <ContentContainer marginY={{ base: '16px', md: '96px' }}>
              <Box display='flex' flexDirection={{ base: 'column', lg: 'row' }} gap={{ base: '24px', md: '32px' }}>
                <Box width={{ base: '100%', sm: 'fit-content', lg: '100%' }}>
                  <ProcessStepsTableOfContents processSteps={caseStudyV2Content.processSteps} />
                </Box>

                <Box>
                  {caseStudyV2Content.processSteps.map((processStep, index) => (
                    <Box key={processStep.title}>
                      {index !== 0 && <Divider marginY={{ base: '48px', md: '80px' }} />}

                      <ProcessStep processStep={processStep} />
                    </Box>
                  ))}
                </Box>
              </Box>
            </ContentContainer>

            {caseStudyV2Content.form && (
              <Box backgroundColor='accent.sky.100' overflow='hidden' paddingY={{ base: '48px', lg: '64px' }}>
                <ContentContainer>
                  <CaseStudyForm
                    ref={formRef}
                    fileUrl={fileUrl}
                    formDescription={caseStudyV2Content.formDescription}
                    leadCaptureType={caseStudyV2Content.leadCaptureType}
                    templateForm={caseStudyV2Content.form}
                  />
                </ContentContainer>
              </Box>
            )}

            {caseStudyV2Content.relatedCaseStudies && (
              <ContentContainer marginY={{ base: '48px', md: '80px' }}>
                <RelatedCaseStudiesSection relatedCaseStudies={caseStudyV2Content.relatedCaseStudies} />
              </ContentContainer>
            )}

            <ShapesBanner {...caseStudyV2Content.shapesBanner} onPrimaryCtaClick={handlePrimaryCtaClick} />
          </Box>
        </PageLayout>
      </>
    );
  }

  if (caseStudyContent) {
    return (
      <>
        <PageHead page={page} pageCategory={PageCategory.CaseStudy} />
        <PageLayout withoutVerticalPadding>
          <CaseStudyHero heroImage={caseStudyContent.heroImage.image} title={caseStudyContent.title} />
          <ContentContainer>
            <Box
              display='flex'
              flexDirection={{ base: 'column-reverse', md: 'row' }}
              flexWrap={{ base: 'wrap', md: 'nowrap' }}
              marginTop={{ base: '40px', md: '80px' }}
            >
              <Box flexBasis={{ base: '100%', md: '40%' }}>
                <CaseStudyCompanyCard
                  companyHighlights={caseStudyContent.companyHighlights}
                  companyLogo={caseStudyContent.company.hifiLogo}
                  fileLink={caseStudyContent.pdfFile.asset.url}
                  industry={caseStudyContent.companyIndustry}
                />
              </Box>
              <Box
                borderRadius={{ base: '0px 24px 0px 0px', md: '0px 24px 4px 0px' }}
                flexBasis={{ base: 'auto', md: '60%' }}
                height={{ base: '300px', md: 'auto' }}
                margin={{ base: '0', md: '64px 0 64px 0' }}
                overflow='hidden'
                position='relative'
              >
                <SanityImage
                  fill={true}
                  image={caseStudyContent.companyImage}
                  sizes='(min-width: 768px) 50vw, 100vw'
                  style={{
                    objectFit: 'cover',
                    objectPosition: 'center',
                  }}
                  priority
                />
              </Box>
            </Box>

            <Flex justifyContent='center'>
              <Box
                alignSelf='center'
                marginBottom={{ base: '40px', md: '80px' }}
                marginTop={{ base: '40px', md: '80px' }}
                maxWidth='736px'
              >
                {caseStudyContent.outcomeSummary && (
                  <Box as='section' marginBottom={{ base: '24px', md: '48px' }}>
                    <Markdown markdownText={caseStudyContent.outcomeSummary} />
                  </Box>
                )}

                <CaseStudyTestimonialCard testimonial={caseStudyContent.testimonial} />
              </Box>
            </Flex>

            {caseStudyContent.processSteps.map((processStep) => (
              <Box key={processStep.title} marginBottom={{ base: '24px', md: '40px' }}>
                <CaseStudyProcessStepCard processStep={processStep} />
              </Box>
            ))}
          </ContentContainer>

          <Flex
            justifyContent='center'
            marginBottom={{ base: '80px', md: '120px' }}
            marginTop={{ base: '40px', md: '80px' }}
          >
            <CaseStudySocialLinks />
          </Flex>

          <ShapesBanner
            {...caseStudyContent.shapesBanner}
            onPrimaryCtaClick={() =>
              trackEvent({
                type: 'click',
                name: 'Enterprise CTA Clicked',
                label: 'Enterprise Telenor Case Study Page CTA',
                action: 'request_a_consult_clicked',
              })
            }
          />
        </PageLayout>
      </>
    );
  }
}

export async function getStaticPaths(): Promise<GetStaticPathsResult> {
  return {
    paths: [],
    fallback: 'blocking',
  };
}

export async function getStaticProps({ params }: GetStaticPropsContext<{ slug: string }>) {
  const caseStudySlug = params?.slug;

  if (!caseStudySlug) {
    logger.fatal('Legal slug not found', {
      file: 'src/pages/case-study/[slug].tsx',
      function: 'getStaticProps',
      caseStudySlug,
    });

    return {
      notFound: true,
    };
  }

  const [page, caseStudyV2Content, caseStudyContent] = await Promise.all([
    getPage('/case-study'),
    getCaseStudyV2Content(caseStudySlug),
    getCaseStudyContent(caseStudySlug),
  ]);

  if (!caseStudyV2Content && !caseStudyContent) {
    logger.fatal('caseStudyContent not found', {
      file: 'src/pages/case-study/[slug].tsx',
      function: 'getStaticProps',
    });

    return {
      notFound: true,
    };
  }

  // If caseStudyContent has seo, override default page seo
  if (caseStudyV2Content) {
    if (caseStudyV2Content?.seo) {
      page.seo = caseStudyV2Content.seo;
    }
  } else if (caseStudyContent?.seo) {
    page.seo = caseStudyContent.seo;
  }

  return {
    props: {
      page,
      caseStudyContent: caseStudyV2Content ? null : caseStudyContent, // Only return this data if caseStudyV2 doesn't exist for the slug
      caseStudyV2Content: caseStudyV2Content ?? null,
    },
  };
}
